import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'

let lockTimer: NodeJS.Timeout | null = null
let lockTimeoutMinutes = 15 // Valeur par défaut
let lastActivityTime = Date.now()
let isAutoLockActive = false // Flag pour éviter les appels multiples

export function useAutoLock() {
  const router = useRouter()

  // Charger le temps de verrouillage depuis general_settings
  const loadLockTimeout = async () => {
    try {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api'
      console.log('[AutoLock] Tentative de chargement depuis:', `${apiBaseUrl}/general-settings`)
      const response = await fetch(`${apiBaseUrl}/general-settings`, { credentials: 'include' })

      console.log('[AutoLock] Response status:', response.status)
      if (response.ok) {
        const settings = await response.json()
        console.log('[AutoLock] Settings reçus:', settings)
        console.log('[AutoLock] tempsVerrou dans settings:', settings.tempsVerrou)
        if (settings.tempsVerrou && settings.tempsVerrou > 0) {
          lockTimeoutMinutes = settings.tempsVerrou
          console.log(`[AutoLock] ✅ Temps de verrouillage configuré: ${lockTimeoutMinutes} minutes`)
        } else {
          console.warn(
            '[AutoLock] tempsVerrou invalide ou manquant, utilisation de la valeur par défaut:',
            lockTimeoutMinutes,
          )
        }
      } else {
        console.error('[AutoLock] ❌ Erreur lors du chargement des settings:', response.status)
      }
    } catch (error) {
      console.error('[AutoLock] ❌ Erreur lors du chargement du temps de verrouillage:', error)
    }
  }

  // Vérifier l'inactivité
  const checkInactivity = async () => {
    // Recharger le temps de verrouillage à chaque vérification
    await loadLockTimeout()

    const now = Date.now()
    const inactiveTime = now - lastActivityTime
    const lockTimeoutMs = lockTimeoutMinutes * 60 * 1000

    const inactiveMinutes = Math.floor(inactiveTime / 60000)
    console.log(`Inactivité: ${inactiveMinutes} min / ${lockTimeoutMinutes} min`)

    if (inactiveTime >= lockTimeoutMs) {
      console.log('🔒 Verrouillage de la session...')
      lockScreen()
    }
  }

  // Verrouiller l'écran
  const lockScreen = () => {
    const currentRoute = router.currentRoute.value.fullPath

    // Ne pas verrouiller si déjà sur une page publique
    const publicPages = ['/auth/login', '/auth/signup', '/auth/recover-password', '/auth/lock']
    if (publicPages.includes(currentRoute)) {
      return
    }

    // Sauvegarder la route actuelle
    sessionStorage.setItem('lockedRoute', currentRoute)

    // Rediriger vers la page de verrouillage
    router.push({ name: 'lock' })
  }

  // Réinitialiser le timer d'inactivité
  const resetInactivityTimer = () => {
    lastActivityTime = Date.now()
  }

  // Événements à surveiller pour détecter l'activité
  const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']

  const startAutoLock = async () => {
    // Éviter les appels multiples
    if (isAutoLockActive) {
      console.log('[AutoLock] ⚠️ Auto-lock déjà actif, ignoré')
      return
    }

    console.log('[AutoLock] 🔒 Démarrage du système de verrouillage automatique...')

    // Charger le temps de verrouillage
    await loadLockTimeout()

    console.log(`[AutoLock] ✅ Auto-lock activé, timeout: ${lockTimeoutMinutes} minutes`)

    // Marquer comme actif
    isAutoLockActive = true

    // Ajouter les écouteurs d'événements
    activityEvents.forEach((event) => {
      document.addEventListener(event, resetInactivityTimer, true)
    })

    // Vérifier l'inactivité toutes les 10 secondes
    if (lockTimer) {
      clearInterval(lockTimer)
    }
    lockTimer = setInterval(checkInactivity, 10000)
  }

  const stopAutoLock = () => {
    console.log('[AutoLock] 🔓 Arrêt du système de verrouillage')

    // Supprimer les écouteurs d'événements
    activityEvents.forEach((event) => {
      document.removeEventListener(event, resetInactivityTimer, true)
    })

    // Arrêter le timer
    if (lockTimer) {
      clearInterval(lockTimer)
      lockTimer = null
    }

    // Marquer comme inactif
    isAutoLockActive = false
  }

  return {
    startAutoLock,
    stopAutoLock,
    lockScreen,
  }
}
